import React, { useEffect, useState } from 'react';
import { Channel } from '@meradio/shared-types';
import { useAppDispatch, useAppSelector } from '@/store/store';
import {
  getFollowedChannels,
  selectFollowedChannels,
  selectIsChannelsLoading,
  selectChannelsError
} from '../channelsSlice';
import ChannelCard from './ChannelCard';
import { Button, Input, Select } from '@/components/ui';

interface ChannelListProps {
  onChannelSelect?: (channel: Channel) => void;
  showSearch?: boolean;
  className?: string;
}

type SortOption = 'name' | 'dateAdded' | 'lastChecked';
type FilterOption = 'all' | 'recentlyChecked' | 'needsCheck';

const ChannelList: React.FC<ChannelListProps> = ({
  onChannelSelect,
  className = "",
}) => {
  const dispatch = useAppDispatch();
  const followedChannels = useAppSelector(selectFollowedChannels);
  const isLoading = useAppSelector(selectIsChannelsLoading);
  const error = useAppSelector(selectChannelsError);

  const [sortBy, setSortBy] = useState<SortOption>('name');
  const [filterBy, setFilterBy] = useState<FilterOption>('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    dispatch(getFollowedChannels());
  }, [dispatch]);

  const filteredAndSortedChannels = React.useMemo(() => {
    let filtered = [...followedChannels];

    if (searchTerm.trim()) {
      filtered = filtered.filter(channel =>
        channel.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (channel.description && channel.description.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    const now = new Date();
    const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);

    switch (filterBy) {
      case 'recentlyChecked':
        filtered = filtered.filter(channel =>
          channel.lastCheckedAt && new Date(channel.lastCheckedAt) > oneDayAgo
        );
        break;
      case 'needsCheck':
        filtered = filtered.filter(channel =>
          !channel.lastCheckedAt || new Date(channel.lastCheckedAt) <= oneDayAgo
        );
        break;
      default:
        break;
    }

    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'dateAdded':
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        case 'lastChecked':
          if (!a.lastCheckedAt && !b.lastCheckedAt) return 0;
          if (!a.lastCheckedAt) return 1;
          if (!b.lastCheckedAt) return -1;
          return new Date(b.lastCheckedAt).getTime() - new Date(a.lastCheckedAt).getTime();
        default:
          return 0;
      }
    });

    return filtered;
  }, [followedChannels, searchTerm, filterBy, sortBy]);

  const handleChannelSelect = (channel: Channel) => {
    if (onChannelSelect) {
      onChannelSelect(channel);
    }
  };

  const handleRetry = () => {
    dispatch(getFollowedChannels());
  };

  return (
    <div className={`w-full ${className}`}>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-meradio-dark mb-2">
          Followed Channels
        </h2>
        <p className="text-meradio-dark-light">
          Your followed YouTube music channels ({followedChannels.length})
        </p>
      </div>

      {followedChannels.length > 0 && (
        <div className="mb-6 space-y-4 md:space-y-0 md:flex md:items-center md:justify-between">
          <div className="flex-1 max-w-md">
            <Input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search followed channels..."
              leftIcon={
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              }
            />
          </div>

          <div className="flex items-center space-x-4">
            <Select
              value={filterBy}
              onChange={(e) => setFilterBy(e.target.value as FilterOption)}
              className="w-auto text-sm"
            >
              <option value="all">All Channels</option>
              <option value="recentlyChecked">Recently Checked</option>
              <option value="needsCheck">Needs Check</option>
            </Select>

            <Select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as SortOption)}
              className="w-auto text-sm"
            >
              <option value="name">Sort by Name</option>
              <option value="dateAdded">Sort by Date Added</option>
              <option value="lastChecked">Sort by Last Checked</option>
            </Select>
          </div>
        </div>
      )}

      <div className="min-h-[400px]">
        {isLoading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, index) => (
              <div
                key={index}
                className="bg-white border border-meradio-light rounded-xl p-4 animate-pulse"
              >
                <div className="flex items-start space-x-3">
                  <div className="w-16 h-16 bg-meradio-light rounded-full flex-shrink-0" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-meradio-light rounded w-3/4" />
                    <div className="h-3 bg-meradio-light rounded w-full" />
                    <div className="h-3 bg-meradio-light rounded w-2/3" />
                  </div>
                  <div className="w-20 h-8 bg-meradio-light rounded" />
                </div>
              </div>
            ))}
          </div>
        )}

        {error && !isLoading && (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">⚠️</div>
            <h3 className="text-lg font-semibold text-meradio-dark mb-2">
              Failed to Load Channels
            </h3>
            <p className="text-meradio-dark-light mb-4 max-w-md mx-auto">
              {error}
            </p>
            <Button variant="primary" onClick={handleRetry}>
              Try Again
            </Button>
          </div>
        )}

        {!isLoading && !error && followedChannels.length === 0 && (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">📺</div>
            <h3 className="text-lg font-semibold text-meradio-dark mb-2">
              No Followed Channels
            </h3>
            <p className="text-meradio-dark-light mb-4 max-w-md mx-auto">
              You haven't followed any YouTube channels yet. Start by searching for your favorite music channels.
            </p>
          </div>
        )}

        {!isLoading && !error && followedChannels.length > 0 && filteredAndSortedChannels.length === 0 && (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🔍</div>
            <h3 className="text-lg font-semibold text-meradio-dark mb-2">
              No Matching Channels
            </h3>
            <p className="text-meradio-dark-light mb-4 max-w-md mx-auto">
              No channels match your current search and filter criteria.
            </p>
            <Button
              variant="link"
              size="sm"
              onClick={() => {
                setSearchTerm('');
                setFilterBy('all');
              }}
            >
              Clear Filters
            </Button>
          </div>
        )}

        {!isLoading && !error && filteredAndSortedChannels.length > 0 && (
          <>
            {(searchTerm || filterBy !== 'all') && (
              <div className="mb-4 text-sm text-meradio-dark-light">
                Showing {filteredAndSortedChannels.length} of {followedChannels.length} channels
              </div>
            )}
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredAndSortedChannels.map((channel) => (
                <ChannelCard
                  key={channel.channelId}
                  channel={channel}
                  onChannelClick={handleChannelSelect}
                  showFollowButton={true}
                  size="md"
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ChannelList;
